import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { NgxEditorModule } from 'ngx-editor';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { AlertModalCheckComponent } from './components/alert-modal-check/alert-modal-check.component';
import { AlertModalComponent } from './components/alert-modal/alert-modal.component';
import { BillingDetailsComponent } from './components/billing-details/billing-details.component';
import { ButtonZapiComponent } from './components/button-zapi/button-zapi.component';
import { CallPhoneComponent } from './components/call-phone/call-phone.component';
import { ChatWaPreviewComponent } from './components/chat-wa-preview/chat-wa-preview.component';
import { ConfirmationLegacyComponent } from './components/confirmation-legacy/confirmation-legacy.component';
import { ConfirmationComponent } from './components/confirmation/confirmation.component';
import { ContactGroupEditComponent } from './components/contact-group/contact-group-edit/contact-group-edit.component';
import { ContactEditComponent } from './components/contact/contact-edit/contact-edit.component';
import { ContactListModalComponent } from './components/contact/contact-list-modal/contact-list-modal.component';
import { ContactListViewModalComponent } from './components/contact/contact-list-view-modal/contact-list-view-modal.component';
import { ContactUploadCsvComponent } from './components/contact/contact-upload-csv/contact-upload-csv.component';
import { ContentLoadingAttendanceCardComponent } from './components/content-loading/content-loading-attedance-card/content-loading-attedance-card.component';
import { ContentLoadingCardComponent } from './components/content-loading/content-loading-card/content-loading-card.component';
import { ContentLoadingListComponent } from './components/content-loading/content-loading-list/content-loading-list.component';
import { CriticalAlertComponent } from './components/critical-alert/critical-alert.component';
import { DraggableFabComponent } from './components/draggable-fab/draggable-fab.component';
import { EditBasicInfoCompanyComponent } from './components/edit-basic-info-company/edit-basic-info-company.component';
import { EditBasicInfoUserComponent } from './components/edit-basic-info-user/edit-basic-info-user.component';
import { EmptyRecordsLegacyComponent } from './components/empty-records-legacy/empty-records-legacy.component';
import { EmptyRecordsComponent } from './components/empty-records/empty-records.component';
import { FieldErrorComponent } from './components/field-error/field-error.component';
import { GoogleMapsViewComponent } from './components/google-maps-view/google-maps-view.component';
import { GoogleMapsComponent } from './components/google-maps/google-maps.component';
import { InfoComponent } from './components/info/info.component';
import { LoadingComponent } from './components/loading/loading.component';
import { TableColumnSortComponent } from './components/table-filter/table-filter.component';
import { PageHeadingComponent } from './components/page-heading/page-heading.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { PreviewMediaComponent } from './components/preview-media/preview-media.component';
import { ModalReportAttendanceComponent } from './components/report-attendance/modal-report-attendance.component';
import { ModalReportContactComponent } from './components/report-contact/modal-report-contact.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { ModalResponseAttendanceComponent } from './components/response-attendance/modal-response-attendance.component';
import { BottomSheetResponseFastComponent } from './components/response-fast/bottom-sheet/bottom-sheet-response-fast.component';
import { ModalResponseFastComponent } from './components/response-fast/modal-response-fast.component';
import { CSATModuleEditComponent } from './components/settings-module/csat-module-edit/csat-module-edit.component';
import { CustomerPortfolioModuleEditComponent } from './components/settings-module/customer-portfolio-module-edit/customer-portfolio-module-edit.component';
import { IAScheduleModalComponent } from './components/settings-module/ia-schedule-modal/ia-schedule-modal.component';
import { SettingsModuleComponent } from './components/settings-module/settings-module.component';
import { SettingsWebhookFormComponent } from './components/settings-webhook/settings-webhook-form/settings-webhook-form.component';
import { SettingsWebhookComponent } from './components/settings-webhook/settings-webhook.component';
import { IrrelevanceReopeningModalComponent } from './components/settings/irrelevance-reopening-modal/irrelevance-reopening-modal.component';
import { SettingsAttendanceComponent } from './components/settings/settings-attendance.component';
import { BrNativeDateAdapter } from './date/br-date-adapter';
import { BR_DATE_FORMATS } from './date/br-date-formats';
import { CnpjTypePipe } from './pipe/cnpj.type.pipe';
import { ContactTypePipe } from './pipe/contact.type.pipe';
import { PhonePipe } from './pipe/phone.pipe';
import { ReadableTextPipe } from './pipe/readable-text.pipe';
import { ResponseTypePipe } from './pipe/response.type.pipe';
import { TagTypePipe } from './pipe/tag.type.pipe';
import { TimezoneDatePipe } from './pipe/timezone.pipe';
import { TruncatePipe } from './pipe/truncate.pipe';


const MATERIAL = [
  MatAutocompleteModule,
  MatBadgeModule,
  MatBottomSheetModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatCardModule,
  MatChipsModule,
  MatCheckboxModule,
  MatDatepickerModule,
  MatGridListModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatNativeDateModule,
  MatPaginatorModule,
  MatProgressBarModule,
  MatRadioModule,
  MatSelectModule,
  MatSidenavModule,
  MatSnackBarModule,
  MatTableModule,
  MatTabsModule,
  MatGridListModule,
  MatSidenavModule,
  MatToolbarModule,
  MatListModule,
  MatFormFieldModule,
  MatDatepickerModule,
  MatDialogModule,
  MatSlideToggleModule,
  MatProgressSpinnerModule,
  MatExpansionModule,
  MatTooltipModule,
  MatBottomSheetModule,
  MatStepperModule,
];

const DIRECTIVES = [
];

const PIPES = [
  TagTypePipe,
  ResponseTypePipe,
  ContactTypePipe,
  TruncatePipe,
  CnpjTypePipe,
  TimezoneDatePipe,
  PhonePipe,
  ReadableTextPipe
];

const COMPONENTS = [
  BillingDetailsComponent,
  ConfirmationComponent,
  ConfirmationLegacyComponent,
  CriticalAlertComponent,
  ContactEditComponent,
  ContactGroupEditComponent,
  ContactListModalComponent,
  EmptyRecordsComponent,
  EmptyRecordsLegacyComponent,
  PaginatorComponent,
  ModalResponseFastComponent,
  LoadingComponent,
  SettingsAttendanceComponent,
  SettingsModuleComponent,
  PreviewMediaComponent,
  AlertModalComponent,
  ContentLoadingListComponent,
  ContentLoadingCardComponent,
  ContentLoadingAttendanceCardComponent,
  ModalResponseAttendanceComponent,
  ModalReportContactComponent,
  ModalReportAttendanceComponent,
  FieldErrorComponent,
  ContactUploadCsvComponent,
  AlertModalCheckComponent,
  CSATModuleEditComponent,
  IAScheduleModalComponent,
  ResetPasswordComponent,
  GoogleMapsComponent,
  GoogleMapsViewComponent,
  ContactListViewModalComponent,
  BottomSheetResponseFastComponent,
  CallPhoneComponent,
  ButtonZapiComponent,
  CustomerPortfolioModuleEditComponent,
  IrrelevanceReopeningModalComponent,
  PageHeadingComponent,
  SettingsWebhookFormComponent,
  SettingsWebhookComponent,
  ChatWaPreviewComponent,
  InfoComponent,
  EditBasicInfoUserComponent,
  EditBasicInfoCompanyComponent,
  TableColumnSortComponent,
  DraggableFabComponent,
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    PickerModule,
    ContentLoaderModule,
    GoogleMapsModule,
    NgxEditorModule.forRoot({
      locals: {
        insertLink: 'Adicionar Link',
        removeLink: 'Remover Link',
        // pupups, forms, others...
        url: 'URL',
        text: 'Texto',
        openInNewTab: 'Abrir em nova aba',
        insert: 'Inserir',
        title: 'Título',
        remove: 'Remover',
        enterValidUrl: 'URL inválida',
      }
    }),
    ...MATERIAL,
    TranslateModule,
  ],
  declarations: [
    ...DIRECTIVES,
    ...PIPES,
    ...COMPONENTS,
  ],
  providers: [
    provideNgxMask(),
    { provide: DateAdapter, useClass: BrNativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: BR_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
  ],
  exports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ContentLoaderModule,
    GoogleMapsModule,
    ...MATERIAL,
    ...PIPES,
    ...COMPONENTS,
    TranslateModule,
  ],
  schemas: []
})
export class SharedModule { }

